/**
 * One save control, read and redrawn.
 *
 * Apart from `save-word.ts`, which is the page and the presses on it, and apart
 * from `save-word-route.ts`, which is what the handler is asked. This is the
 * markup. Which elements a control is made of, what its button says in each
 * state, and how a press is posted where the route could not be reached.
 *
 * `src/user/save-word-markup.ts` and `partials/cidian-save-word.html` are what
 * write that markup, in a Lambda and in Hugo. The classes below are the whole
 * of the contract between the three.
 */

import { wordField } from "./save-word-route.js";

/** The button one control is pressed by. */
const buttonSelector = ".cb-save-word-action";

/** The line offering an account, `hidden` until a signed-out reader presses. */
const offerSelector = ".cb-save-word-offer";

/**
 * What the button says in each of the two states.
 *
 * The same two words `savedWordActions` in `src/user/save-word-markup.ts`
 * writes. This bundle shares no module with the handler, being compiled by
 * `tsc -p src/site` for a browser, so the strings are written twice.
 * `save-word.test.ts` draws its fixture with `savedWordControl` and reads the
 * labels back, which is what fails when the two come apart.
 */
const actions = { save: "Save", held: "Saved" };

/**
 * The name the button carries for somebody who cannot see it.
 *
 * The visible word comes first, which is what a reader saying the name of a
 * control to their machine has to be able to say. WCAG 2.5.3 is the rule.
 */
function label(word: string, held: boolean): string {
  return held
    ? `${actions.held} ${word}, press to remove`
    : `${actions.save} ${word}`;
}

/** The button of one control, where it has one. */
export function buttonOf(control: HTMLElement): HTMLButtonElement | null {
  return control.querySelector<HTMLButtonElement>(buttonSelector);
}

/** Draw one button in the state the route reported. */
export function drawSavedWord(button: HTMLButtonElement, held: boolean): void {
  button.textContent = held ? actions.held : actions.save;
  button.setAttribute("aria-label", label(button.value, held));

  if (held) {
    button.dataset["cbSaved"] = "yes";
  } else {
    delete button.dataset["cbSaved"];
  }
}

/** Show the account offer under one control. */
export function revealAccountOffer(control: HTMLElement): void {
  const offer = control.querySelector<HTMLElement>(offerSelector);

  if (offer !== null) {
    offer.hidden = false;
  }
}

/**
 * Post the press the way the form would have.
 *
 * **The word is on the button and `submit()` posts no button**, so the value
 * moves to a hidden field first. Without that the fallback would post an empty
 * form and the reader's press would be lost, which is worse than the failure it
 * is recovering from.
 */
export function postWithoutScript(form: HTMLFormElement, word: string): void {
  const carried = form.ownerDocument.createElement("input");

  carried.type = "hidden";
  carried.name = wordField;
  carried.value = word;
  form.append(carried);
  form.submit();
}
